import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { requestVersionKeys } from './useRequestVersionsQuery';
import { getAuthError } from '../../lib/queryClient';
import {
  RequestsService,
  PaginationParams,
  PaginatedResponse,
} from '../../services';
import { Request, CreateRequestData, UpdateRequestData } from '../../types';

// Query keys for consistent cache management
export const requestsKeys = {
  all: ['requests'] as const,
  lists: () => [...requestsKeys.all, 'list'] as const,
  list: (filters: string) => [...requestsKeys.lists(), { filters }] as const,
  paginated: (params: PaginationParams) =>
    [...requestsKeys.all, 'paginated', params] as const,
  details: () => [...requestsKeys.all, 'detail'] as const,
  detail: (id: string) => [...requestsKeys.details(), id] as const,
};

/**
 * Hook for fetching all requests with React Query
 */
export const useRequestsQuery = () => {
  return useQuery({
    queryKey: requestsKeys.lists(),
    queryFn: RequestsService.getAllRequests,
    meta: {
      errorMessage: 'Failed to fetch requests. Please check your connection.',
    },
  });
};

/**
 * Hook for fetching paginated requests with React Query
 * Includes smart background polling controlled by environment variable
 * Features auth-aware placeholder data to prevent showing stale content after session expiration
 */
export const useRequestsPaginatedQuery = (
  params: PaginationParams,
  /**
   * `staleTime` override, for a caller that knows the cached page may have been invalidated
   * by something this query cannot see. `ViewRequests` passes 0 while the Read Status filter
   * is on (#1532 review, Kiran issue 2) -- opening a request marks it read, so the cached
   * "Unread" page is wrong the moment the person comes back. Omit it for the 30s default.
   */
  options: { staleTime?: number } = {},
) => {
  const queryClient = useQueryClient();

  // Get refetch interval from environment variable (in minutes), default to 10 minutes
  // Set to 0 to disable polling
  const refetchIntervalMinutes = parseInt(
    process.env.REACT_APP_REFETCH_INTERVAL_MINUTES || '10',
    10,
  );
  const refetchIntervalMs =
    refetchIntervalMinutes > 0 ? refetchIntervalMinutes * 60 * 1000 : false;

  // Don't run query if userRoles is empty - wait for auth to load
  const hasValidAuth = params.userRoles && params.userRoles.length > 0;

  return useQuery({
    queryKey: requestsKeys.paginated(params),
    queryFn: () => RequestsService.getRequestsPaginated(params),
    enabled: hasValidAuth, // Only run query when user is authenticated with roles
    staleTime: options.staleTime ?? 30 * 1000, // 30s default; see `options` above
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
    refetchOnWindowFocus: true, // Refetch when returning to tab for fresh data
    refetchOnMount: true, // Refetch on mount for fresh data
    refetchOnReconnect: true, // Refetch when connection restored
    retry: false, // Don't retry on failure - let offline mode handle it gracefully
    networkMode: 'always', // Allow query to run even when offline - will fail gracefully

    // SMART PLACEHOLDER DATA: Only show if not auth error AND data is fresh
    // This prevents showing stale request data when session has expired or after long periods away
    placeholderData: (previousData, previousQuery) => {
      // Check global auth error flag (set by query client on 401)
      if (getAuthError()) {
        return undefined;
      }

      // Check if previous query failed with auth error
      const error: any = previousQuery?.state.error;
      if (error?.status === 401 || error?.response?.status === 401) {
        return undefined;
      }

      // Check if data is too stale (older than 5 minutes)
      const dataUpdatedAt = previousQuery?.state.dataUpdatedAt || 0;
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (now - dataUpdatedAt > fiveMinutes) {
        return undefined;
      }

      return previousData;
    },

    // Background polling: configurable via environment variable
    // React Query automatically pauses polling when tab is hidden and offline
    refetchInterval: refetchIntervalMs,
    refetchIntervalInBackground: false, // Pause polling when tab is hidden

    // Custom error handling for auth errors
    onError: (error: any) => {
      if (error?.status === 401 || error?.response?.status === 401) {
        // Cache already cleared by query client
      }
    },

    meta: {
      errorMessage: 'Failed to fetch requests. Showing cached data.',
    },
  });
};

/**
 * Hook for fetching a single request by ID
 */
export const useRequestQuery = (id: string) => {
  return useQuery({
    queryKey: requestsKeys.detail(id),
    queryFn: () => RequestsService.getRequestById(id),
    enabled: !!id, // Only run query if ID is provided
    meta: {
      errorMessage: 'Failed to fetch request details.',
    },
  });
};

/**
 * Hook for creating a new request
 */
export const useCreateRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: RequestsService.createRequest,
    onSuccess: newRequest => {
      // Invalidate and refetch requests list
      queryClient.invalidateQueries({ queryKey: requestsKeys.lists() });

      // Add the new request to the cache
      queryClient.setQueryData(requestsKeys.detail(newRequest.id), newRequest);
    },
    meta: {
      errorMessage: 'Failed to create request. Please try again.',
    },
  });
};

/**
 * Hook for updating a request
 */
export const useUpdateRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRequestData }) =>
      RequestsService.updateRequest(id, data),
    onSuccess: updatedRequest => {
      // Update the specific request in cache
      queryClient.setQueryData(
        requestsKeys.detail(updatedRequest.id),
        updatedRequest,
      );

      // Invalidate the requests list to reflect changes
      queryClient.invalidateQueries({ queryKey: requestsKeys.lists() });

      // A save captures a new version server-side (#1148) — refresh version history.
      queryClient.invalidateQueries({ queryKey: requestVersionKeys.all });
    },
    meta: {
      errorMessage: 'Failed to update request. Please try again.',
    },
  });
};

/**
 * Hook for deleting a request
 */
export const useDeleteRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: RequestsService.deleteRequest,
    onSuccess: (_, deletedId) => {
      // Remove the request from cache
      queryClient.removeQueries({ queryKey: requestsKeys.detail(deletedId) });

      // Invalidate the requests list
      queryClient.invalidateQueries({ queryKey: requestsKeys.lists() });
    },
    meta: {
      errorMessage: 'Failed to archive request. Please try again.',
    },
  });
};
